import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import { MOCK_SUCCESS_CB_RESULTS } from 'data/claim-business-mock-results';

// Define types
export type BusinessInfo = {
    business_name: string;
    trade_name: string;
    uei: string;
    business_address: string;
    government_contact: string;
}

type ClaimRequestId = string;

type ClaimBusinessState = {
    data: BusinessInfo[] | null;
    claimRequestId: ClaimRequestId | null;

    setData: (data: BusinessInfo[]) => void;
    setClaimRequestId: (claimRequestId: ClaimRequestId) => void;

    claimBusiness: ({claimRequestId, structure }: {claimRequestId: string; structure: string}) => Promise<void>;

    reset: () => void;
};

const useClaimBusinessStore = create<ClaimBusinessState>((set) => ({
    data: null,
    claimRequestId: null,

    setData: (data) => set({ data }),
    setClaimRequestId: (claimRequestId) => set({ claimRequestId }),

    claimBusiness: async ({ claimRequestId, structure }) => {
        try {
            const response = await fetch(
              "/_api/ucp/claim-business",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  claim_request_id: claimRequestId,
                  structure,
                }),
              }
            );

            // handle network errors or invalid json
            let result;

            try {
                result = await response.json();
            } catch (parseError) {
                throw new Error('Invalid response format from server');
            }

            if (!response.ok) {
                const message = result?.message || result?.error || `Request failed with status ${response.status}`;
                throw new Error(message);
            }

            // Reset the store after successful claim
            set({ data: null, claimRequestId: null });
            return result;
        } catch (error) {
            // Re-throw network error
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error('Network error: Unable to connect to server');
            }

            // Re-throw the original error
            throw error;
        }
    },

    reset: () => set({ data: null, claimRequestId: null }),
}));

export default useClaimBusinessStore;